import styles from './CaseCard.module.css'
import CaseMap from './CaseMap'

// created/resolved arrive as ISO strings; render "Jun 3, 2026 · 06:42". Falls back
// to the raw string (or an em dash) so a malformed/absent value never throws.
function formatDateTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
  return `${date} · ${time}`
}

/**
 * CaseCard — one case summary in the Cases list. The whole card is clickable and
 * opens the case details screen (the mockup's "Details" button is intentionally
 * omitted). It renders a header (id, title, resolution-quality score), a footer of
 * case facts, and — at the foot of the card — the shared Symptom → Problem → Root Cause
 * map (CaseMap, also used on the detail page).
 *
 * Props:
 *   caseData {object}   — one case from POST /api/cases/list
 *   onOpen   {function} — called (no args) when the card is activated
 */
export default function CaseCard({ caseData, onOpen }) {
  const {
    number, title, created_date_time, resolved_date_time,
    priority, team, resolved_by, symptoms = [], problems = [],
    resolution_code, qos_score, qos_score_text,
  } = caseData

  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() }
  }

  return (
    <article
      className={styles.card}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      aria-label={`Open case ${number}`}
    >
      {/* ── Header ─────────────────────────────── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.caseId}>{number}</span>
          <span className={styles.caseTitle}>{title}</span>
        </div>
        {qos_score != null && (
          <div className={styles.qos}>
            {qos_score_text && <span className={styles.qosLabel}>{qos_score_text}</span>}
            <span className={styles.qosValue}>{qos_score}%</span>
          </div>
        )}
      </div>

      {/* ── Footer ─────────────────────────────── */}
      <div className={styles.footer}>
        <FooterField label="Created" value={formatDateTime(created_date_time)} />
        <FooterField label="Closed" value={formatDateTime(resolved_date_time)} />
        <FooterField label="Priority" value={priority || '—'} />
        <FooterField label="Team" value={team || '—'} />
        <FooterField label="Resolving Agent" value={resolved_by || '—'} />
      </div>

      {/* ── Symptom → Problem → Root Cause map (foot of the card) ─ */}
      {/* stopPropagation so expanding "+N more" doesn't also open the case. */}
      <CaseMap
        symptoms={symptoms}
        problems={problems}
        resolutionCode={resolution_code}
        stopPropagation
      />
    </article>
  )
}

function FooterField({ label, value }) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={styles.fieldValue}>{value}</span>
    </div>
  )
}
